import { AxiosResponse } from 'axios';
import api from '../api';
import { IExamReportCardResponse } from '../../interface/reportCard.interface';
import { REPORT_CARD_PDF_TIMEOUT_MS } from '../../utility/downloadReportCard';
import {
  ExamDetailsResponse,
  ExamByIdDetailsResponse,
  CreateExamRequest,
  CreateExamResponse,
  ExamPayload,
  UpdateExamResponse,
  ExamSubjectDetailsResponse,
  PublishExamResponse,
  CompleteExamResponse,
  CancelExamResponse,
  DeleteExamResponse,
  IExamAcknowledgementParams,
  IExamAcknowledgementResponse,
  StartMarkingExamSubjectResponse,
  PatchExamSubjectMarksRequest,
  PatchExamSubjectMarksResponse,
  CompleteMarkingExamSubjectResponse,
} from '../../interface/examDetails.interface';
import {
  CloneExamPaperResponse,
  FetchExamPaperByIdResponse,
  FetchExamPaperResponse,
  FetchReusablePapersParams,
  FetchReusablePapersResponse,
  UpsertExamPaperRequest,
  UpsertExamPaperResponse,
} from '../../interface/examPaper.interface';
import {
  GetAttemptForMarkingResponse,
  PatchExamItemMarkRequest,
  PatchExamItemMarkResponse,
  PatchExamAttemptMarksRequest,
  PatchExamAttemptMarksResponse,
  ReleaseAttemptMarksResponse,
} from '../../interface/examMarking.interface';

export const getExams = async (params?: ExamPayload): Promise<ExamDetailsResponse> => {
  const response = await api.get<ExamDetailsResponse>('/exam', { params });
  return response.data;
};

export const getExamById = async (id: string): Promise<ExamByIdDetailsResponse> => {
  const response = await api.get<ExamByIdDetailsResponse>(`exam/${id}`);
  return response.data;
};

export const addExam = async (payload: CreateExamRequest): Promise<CreateExamResponse> => {
  const response = await api.post<CreateExamResponse>('exam', payload);
  return response.data;
};

export const updateExam = async (
  examId: string,
  body: CreateExamRequest,
): Promise<UpdateExamResponse> => {
  const response = await api.patch<UpdateExamResponse>(`/exam/${examId}`, body);
  return response.data;
};

export const examPublish = async (examId: string): Promise<PublishExamResponse> => {
  const response = await api.patch<PublishExamResponse>(`/exam/${examId}/publish`);
  return response.data;
};

export const examRelease = async (examId: string): Promise<CompleteExamResponse> => {
  const response = await api.patch<CompleteExamResponse>(`/exam/${examId}/complete`);
  return response.data;
};

export const examCancel = async (examId: string): Promise<CancelExamResponse> => {
  const response = await api.patch<CancelExamResponse>(`/exam/${examId}/cancel`);
  return response.data;
};

export const examDelete = async (examId: string): Promise<DeleteExamResponse> => {
  const response = await api.delete<DeleteExamResponse>(`/exam/${examId}`);
  return response.data;
};

export const getExamSubjectById = async (id: string): Promise<ExamSubjectDetailsResponse> => {
  const response = await api.get<ExamSubjectDetailsResponse>(`exam-subject/${id}`);
  return response.data;
};

/* --------------------------- Marking (grading) --------------------------- *
 * All three routes live on the EXISTING `v2/exam-subject` controller and are
 * already used by the mobile app — no backend change accompanies these.
 * Row-level authorization is enforced server-side: Admin/SuperAdmin bypass,
 * everyone else must be the subject's marking teacher.
 * ------------------------------------------------------------------------- */

/** PENDING -> IN_PROGRESS. */
export const startMarkingExamSubject = async (
  examSubjectId: string,
): Promise<StartMarkingExamSubjectResponse> => {
  const response = await api.patch<StartMarkingExamSubjectResponse>(
    `/exam-subject/${examSubjectId}/start-marking`,
  );
  return response.data;
};

/** Bulk upsert of scores/remarks. Requires the subject to be IN_PROGRESS. */
export const patchExamSubjectMarks = async (
  examSubjectId: string,
  body: PatchExamSubjectMarksRequest,
): Promise<PatchExamSubjectMarksResponse> => {
  const response = await api.patch<PatchExamSubjectMarksResponse>(
    `/exam-subject/${examSubjectId}/marks`,
    body,
  );
  return response.data;
};

/**
 * CVA-4197 plan 0011 slice 4 (Flow A canvas). Read-only for now — the marking
 * payload for one student's attempt, joining the frozen snapshot with live
 * `is_correct`/`description` and any already-persisted `exam_item_mark` rows
 * (shipped backend-side in slice 3). The write routes (per-question save,
 * bulk save, release) land in slices 5/6.
 */
export const getAttemptForMarking = async (
  examSubjectId: string,
  studentId: string,
): Promise<GetAttemptForMarkingResponse> => {
  const response = await api.get<GetAttemptForMarkingResponse>(
    `/exam-subject/${examSubjectId}/attempts/${studentId}`,
  );
  return response.data;
};

/**
 * CVA-4197 plan 0011 slice 5 — per-question save for Flow A. Rejected (400)
 * when the item isn't in the paper, is TEXT_BLOCK, has `points: null` with a
 * non-null `score`, or `score > points`. `score < 0` is rejected by the
 * backend DTO itself. Never touches `exam_marks`/its status and is safely
 * repeatable any number of times while the submission is unreleased.
 */
export const patchExamItemMark = async (
  examSubjectId: string,
  studentId: string,
  itemId: string,
  body: PatchExamItemMarkRequest,
): Promise<PatchExamItemMarkResponse> => {
  const response = await api.patch<PatchExamItemMarkResponse>(
    `/exam-subject/${examSubjectId}/attempts/${studentId}/items/${itemId}`,
    body,
  );
  return response.data;
};

/**
 * CVA-4197 plan 0011 slice 5 — bulk save for the rail's own "Save Draft"
 * button. Every item is validated before anything is written — if any one
 * entry is invalid, the whole request is rejected and nothing is persisted.
 * A genuinely separate call from `patchExamItemMark`; never touches
 * `exam_marks`/its status either.
 */
export const patchExamAttemptMarks = async (
  examSubjectId: string,
  studentId: string,
  body: PatchExamAttemptMarksRequest,
): Promise<PatchExamAttemptMarksResponse> => {
  const response = await api.patch<PatchExamAttemptMarksResponse>(
    `/exam-subject/${examSubjectId}/attempts/${studentId}`,
    body,
  );
  return response.data;
};

/**
 * CVA-4197 plan 0011 slice 6 — Release Score for one student's attempt. No
 * request body. Zero-fills any unscored non-TEXT_BLOCK item, aggregates,
 * normalises to a percentage, and upserts `exam_marks` (Q10/Q11). Rejected
 * (400) when an item the student actually answered still has no teacher
 * score, or when this submission was already released. This is the point of
 * no return for this student's submission — no further per-question or bulk
 * save call succeeds afterward.
 */
export const releaseAttemptMarks = async (
  examSubjectId: string,
  studentId: string,
): Promise<ReleaseAttemptMarksResponse> => {
  const response = await api.patch<ReleaseAttemptMarksResponse>(
    `/exam-subject/${examSubjectId}/attempts/${studentId}/release`,
  );
  return response.data;
};

/**
 * IN_PROGRESS -> COMPLETED. Stamps every mark GRADED (has a score) or ABSENT
 * (no score). Writes no scores of its own, so callers MUST persist outstanding
 * edits via `patchExamSubjectMarks` first.
 */
export const completeMarkingExamSubject = async (
  examSubjectId: string,
): Promise<CompleteMarkingExamSubjectResponse> => {
  const response = await api.patch<CompleteMarkingExamSubjectResponse>(
    `/exam-subject/${examSubjectId}/complete-marking`,
  );
  return response.data;
};

export const getExamAcknowledgements = async (
  examId: string,
  params?: IExamAcknowledgementParams,
): Promise<IExamAcknowledgementResponse> => {
  const response = await api.get<IExamAcknowledgementResponse>(`/exam/${examId}/acknowledgement`, {
    params,
  });
  return response.data;
};

/** Staff view of a student's single-exam report card. */
export const getExamReportCard = async (
  examId: string,
  studentId: string,
): Promise<IExamReportCardResponse> => {
  const response = await api.get<IExamReportCardResponse>(
    `/exam/${examId}/report-card/${studentId}`,
  );
  return response.data;
};

/**
 * Staff PDF download of the same report card. Returns the raw response so the
 * caller can read the server-provided filename off Content-Disposition.
 */
export const downloadExamReportCard = async (
  examId: string,
  studentId: string,
): Promise<AxiosResponse<Blob>> => {
  return api.get<Blob>(`/exam/${examId}/report-card/${studentId}/pdf`, {
    responseType: 'blob',
    // Server-side puppeteer render — needs more than the instance's 10s default.
    timeout: REPORT_CARD_PDF_TIMEOUT_MS,
  });
};

// Exam Paper (authoring) API calls. All routes live on the EXISTING
// `v2/exam-subject` controller — NOT a separate `exam-paper` controller —
// so these hit `/exam-subject/...` via the same shared `api` instance,
// exactly like `getExamSubjectById` above.

export const getReusableExamPapers = async (
  params: FetchReusablePapersParams,
): Promise<FetchReusablePapersResponse> => {
  const response = await api.get<FetchReusablePapersResponse>('/exam-subject/reusable-papers', {
    params,
  });
  return response.data;
};

export const getExamPaper = async (examSubjectId: string): Promise<FetchExamPaperResponse> => {
  const response = await api.get<FetchExamPaperResponse>(`/exam-subject/${examSubjectId}/paper`);
  return response.data;
};

/**
 * CVA-4405 — printable PDF of the exam paper (answer keys masked server-side;
 * MCQ option order is NOT shuffled yet, that is deferred to a later phase).
 * Returns the raw response so the caller can read the server-provided
 * filename off Content-Disposition, same pattern as `downloadExamReportCard`.
 */
export const printExamPaper = async (examSubjectId: string): Promise<AxiosResponse<Blob>> => {
  return api.get<Blob>(`/exam-subject/${examSubjectId}/paper/print`, {
    responseType: 'blob',
    // Server-side PDF render — needs more than the instance's 10s default.
    timeout: REPORT_CARD_PDF_TIMEOUT_MS,
  });
};

/**
 * Fetches a paper by its OWN id (not by `examSubjectId`) — needed to preview
 * a chosen "Select an Existing Template" paper's content into a local draft
 * before any `exam_subjects` row exists yet (create flow) or without
 * persisting a clone server-side (edit flow) — see PaperSourceChoice. Backed
 * by the new `GET v2/exam-subject/paper/:paperId` endpoint (see the matching
 * backend implementation plan), which wraps the same
 * `ExamPaperRepository.getPaperFullById` + `ExamPaperUtils.formatPaper` used
 * by `getExamPaper` above, so the response shape is identical.
 */
export const getExamPaperById = async (paperId: string): Promise<FetchExamPaperByIdResponse> => {
  const response = await api.get<FetchExamPaperByIdResponse>(`/exam-subject/paper/${paperId}`);
  return response.data;
};

export const upsertExamPaper = async (
  examSubjectId: string,
  body: UpsertExamPaperRequest,
): Promise<UpsertExamPaperResponse> => {
  const response = await api.put<UpsertExamPaperResponse>(
    `/exam-subject/${examSubjectId}/paper`,
    body,
  );
  return response.data;
};

export const cloneExamPaper = async (
  examSubjectId: string,
  sourcePaperId: string,
): Promise<CloneExamPaperResponse> => {
  const response = await api.post<CloneExamPaperResponse>(
    `/exam-subject/${examSubjectId}/paper/clone/${sourcePaperId}`,
  );
  return response.data;
};
